import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  LayoutDashboard,
  Package,
  BookOpen,
  ShoppingCart,
  History,
  Users,
  Bell,
  BarChart3,
  Settings as SettingsIcon,
  ShieldCheck,
  LogOut
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { logoutUser } from '../services/auth';

const SIDEBAR_WIDTH = '280px';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
`;

const Sidebar = styled.nav`
  width: ${SIDEBAR_WIDTH};
  position: relative;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.94) 100%);
  border-right: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 4px 0 24px -12px rgba(15, 23, 42, 0.08);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 100;
    transform: translateX(${props => (props.$isOpen ? '0' : '-110%')});
    transition: transform 340ms cubic-bezier(0.22, 1, 0.36, 1),
                box-shadow 340ms ease;
    will-change: transform;
    box-shadow: ${props =>
      props.$isOpen
        ? '8px 0 40px -10px rgba(15, 23, 42, 0.25)'
        : 'none'};
  }
`;

const LogoWrap = styled.div`
  padding: 22px 22px 18px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 22px;
    right: 22px;
    bottom: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(148, 163, 184, 0.3) 50%,
      transparent 100%
    );
  }
`;

const LogoBrand = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const LogoSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NavScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 14px 12px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.35);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const SectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #94a3b8;
  padding: 14px 14px 6px;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NavItem = styled.li`
  margin: 0;
`;

const NavLinkStyled = styled(NavLink)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 10px;
  transition:
    background 180ms ease,
    color 180ms ease,
    transform 180ms ease;

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.1;
    flex-shrink: 0;
    transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover {
    background: rgba(79, 179, 191, 0.08);
    color: #2E5B88;

    svg {
      transform: scale(1.08);
    }
  }

  &.active {
    background: linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%);
    color: #ffffff;
    font-weight: 600;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.25) inset,
      0 6px 14px -6px rgba(46, 91, 136, 0.45),
      0 12px 32px -12px rgba(79, 179, 191, 0.4);

    &::before {
      content: '';
      position: absolute;
      left: -14px;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 60%;
      background: linear-gradient(180deg, #4FB3BF 0%, #2E5B88 100%);
      border-radius: 0 3px 3px 0;
    }

    svg {
      stroke-width: 2.4;
    }
  }
`;

const AdminSeparator = styled.div`
  margin: 18px 14px 10px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.3), transparent);
`;

const UserSection = styled.div`
  padding: 14px 16px 18px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  background: linear-gradient(180deg, transparent 0%, rgba(79, 179, 191, 0.04) 100%);
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.15);
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 10px -2px rgba(46, 91, 136, 0.35);
`;

const UserMeta = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .email {
    font-size: 11.5px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 1px;
  }
`;

const LogoutBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: #64748b;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-x: hidden;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(16px) saturate(1.4);
  -webkit-backdrop-filter: blur(16px) saturate(1.4);
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  padding: 10px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(79, 179, 191, 0.45); }
  100% { box-shadow: 0 0 0 10px rgba(79, 179, 191, 0); }
`;

const MobileMenuButton = styled.button`
  display: none;
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
  background: ${props =>
    props.$isOpen
      ? 'linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%)'
      : 'rgba(255, 255, 255, 0.92)'};
  box-shadow: ${props =>
    props.$isOpen
      ? '0 6px 16px -4px rgba(46, 91, 136, 0.45), 0 1px 0 rgba(255,255,255,0.3) inset'
      : '0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 12px -4px rgba(15, 23, 42, 0.08), 0 1px 0 rgba(255,255,255,0.9) inset'};
  transition:
    background 260ms ease,
    box-shadow 260ms ease,
    transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(
      circle at 30% 20%,
      ${props => (props.$isOpen ? 'rgba(255,255,255,0.22)' : 'rgba(79,179,191,0.08)')} 0%,
      transparent 60%
    );
    pointer-events: none;
  }

  &:active {
    transform: scale(0.93);
  }

  &:hover {
    transform: translateY(-1px);
  }

  ${props =>
    props.$isOpen &&
    css`animation: ${MenuPulse} 1.4s ease-out 1;`}

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

const HamburgerBox = styled.span`
  position: relative;
  width: 20px;
  height: 16px;
  display: inline-block;
`;

const HamburgerLine = styled.span`
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  border-radius: 2px;
  background: ${props => (props.$isOpen ? '#ffffff' : '#2E5B88')};
  transition:
    transform 380ms cubic-bezier(0.68, -0.55, 0.265, 1.55),
    top 260ms ease 80ms,
    opacity 200ms ease,
    background 260ms ease,
    width 260ms ease;
  transform-origin: center;

  &:nth-child(1) {
    top: ${props => (props.$isOpen ? '7px' : '0')};
    transform: ${props => (props.$isOpen ? 'rotate(45deg)' : 'rotate(0)')};
    transition-delay: ${props => (props.$isOpen ? '80ms, 0ms' : '0ms, 80ms')};
  }

  &:nth-child(2) {
    top: 7px;
    opacity: ${props => (props.$isOpen ? 0 : 1)};
    width: ${props => (props.$isOpen ? '0' : '70%')};
    transform: translateX(${props => (props.$isOpen ? '10px' : '0')});
  }

  &:nth-child(3) {
    top: ${props => (props.$isOpen ? '7px' : '14px')};
    transform: ${props => (props.$isOpen ? 'rotate(-45deg)' : 'rotate(0)')};
    transition-delay: ${props => (props.$isOpen ? '80ms, 0ms' : '0ms, 80ms')};
  }
`;

const AnimatedHamburger = ({ isOpen }) => (
  <HamburgerBox>
    <HamburgerLine $isOpen={isOpen} />
    <HamburgerLine $isOpen={isOpen} />
    <HamburgerLine $isOpen={isOpen} />
  </HamburgerBox>
);

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => (props.$isOpen ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 99;
    animation: ${fadeIn} 240ms ease;
  }
`;

const getInitials = (name, email) => {
  const source = (name || '').trim() || (email || '').trim();
  if (!source) return 'N';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const primaryNav = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/inventory', label: 'Inventario', icon: Package },
  { to: '/catalog', label: 'Catálogo', icon: BookOpen }
];

const operationsNav = [
  { to: '/sales', label: 'Ventas', icon: ShoppingCart },
  { to: '/sales-history', label: 'Historial de Ventas', icon: History },
  { to: '/customers', label: 'Clientes', icon: Users }
];

const insightsNav = [
  { to: '/alerts', label: 'Alertas', icon: Bell },
  { to: '/reports', label: 'Reportes', icon: BarChart3 },
  { to: '/settings', label: 'Configuración', icon: SettingsIcon }
];

export const Layout = () => {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.email === 'nomoalex@hotmail.com';

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = () => setSidebarOpen(v => !v);

  if (loading) {
    return <div>Cargando...</div>;
  }

  const renderNav = items =>
    items.map(({ to, label, icon: Icon, end }) => (
      <NavItem key={to}>
        <NavLinkStyled to={to} end={end} onClick={closeSidebar}>
          <Icon />
          <span>{label}</span>
        </NavLinkStyled>
      </NavItem>
    ));

  return (
    <LayoutContainer>
      <Overlay $isOpen={sidebarOpen} onClick={closeSidebar} />

      <Sidebar $isOpen={sidebarOpen}>
        <LogoWrap>
          <LogoBrand>NomoFarma</LogoBrand>
          <LogoSubtitle>{userData?.pharmacyName || 'Gestión farmacéutica'}</LogoSubtitle>
        </LogoWrap>

        <NavScroll>
          <SectionLabel>Principal</SectionLabel>
          <NavList>{renderNav(primaryNav)}</NavList>

          <SectionLabel>Operaciones</SectionLabel>
          <NavList>{renderNav(operationsNav)}</NavList>

          <SectionLabel>Análisis</SectionLabel>
          <NavList>{renderNav(insightsNav)}</NavList>

          {isAdmin && (
            <>
              <AdminSeparator />
              <SectionLabel>Administración</SectionLabel>
              <NavList>
                <NavItem>
                  <NavLinkStyled to="/admin/users" onClick={closeSidebar}>
                    <ShieldCheck />
                    <span>Usuarios</span>
                  </NavLinkStyled>
                </NavItem>
              </NavList>
            </>
          )}
        </NavScroll>

        <UserSection>
          <UserCard>
            <Avatar>{getInitials(userData?.pharmacyName, userData?.email)}</Avatar>
            <UserMeta>
              <div className="name">{userData?.pharmacyName || 'Mi farmacia'}</div>
              <div className="email">{userData?.email}</div>
            </UserMeta>
            <LogoutBtn onClick={handleLogout} title="Cerrar sesión" aria-label="Cerrar sesión">
              <LogOut size={18} />
            </LogoutBtn>
          </UserCard>
        </UserSection>
      </Sidebar>

      <MainContent>
        <Header>
          <MobileMenuButton
            onClick={toggleSidebar}
            $isOpen={sidebarOpen}
            aria-label="Menú"
            aria-expanded={sidebarOpen}
          >
            <AnimatedHamburger isOpen={sidebarOpen} />
          </MobileMenuButton>
          <HeaderTitle>{userData?.pharmacyName || 'NomoFarma'}</HeaderTitle>
        </Header>

        <Content>
          <Outlet />
        </Content>
      </MainContent>
    </LayoutContainer>
  );
};
